import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import LiteYouTubeEmbed from 'react-lite-youtube-embed';
import 'react-lite-youtube-embed/dist/LiteYouTubeEmbed.css';
import { getYoutubeVideoId, isYoutubeShort } from '../../utils/youtube';
import { stripWrappingQuotes, OPEN_QUOTE, CLOSE_QUOTE } from '../../utils/text';
import { renderWidgetLayout } from '../../components/WidgetLayouts';
import supabase from '../../supabase/supabase';
import SafeIcon from '../../common/SafeIcon';
import { FiStar, FiMessageSquare, FiAlertTriangle, FiHeart, FiYoutube } from 'react-icons/fi';

// ----------------------------------------------------------------------
// GOOGLE FONTS HOOK
// ----------------------------------------------------------------------
const useGoogleFont = (fontFamily) => {
  useEffect(() => {
    if (!fontFamily) return;
    const fontName = fontFamily.replace(/ /g, '+');
    const url = `https://fonts.googleapis.com/css2?family=${fontName}:wght@300;400;500;600;700;800&display=swap`;
    
    // Check if the font is already loaded (e.g., globally in index.html)
    const existingLink = document.querySelector(`link[href="${url}"]`);
    if (existingLink) return;

    // Create and inject the new font link
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    document.head.appendChild(link);

    // Cleanup: Remove the injected link when the font changes or component unmounts
    return () => {
      if (link.parentNode) {
        link.parentNode.removeChild(link);
      }
    };
  }, [fontFamily]);
};

// ----------------------------------------------------------------------
// CUSTOM ICONS
// ----------------------------------------------------------------------
const DiamondIcon = ({ className, style }) => (
  <svg viewBox="0 0 122.88 96.04" fill="currentColor" className={className || "w-4 h-4"} style={style}>
    <path fillRule="evenodd" clipRule="evenodd" d="M59.07,96.03L1.15,29.77h38.01L59.07,96.03L59.07,96.03L59.07,96.03z M61.18,0.11L45.89,25.24H77.6L61.18,0.11 L61.18,0.11z M82.18,25.07L65.17,0h31.45L82.18,25.07L82.18,25.07z M41.65,25.31L56.55,0H24.61L41.65,25.31L41.65,25.31z M101.71,2.98L86.67,25.24h36.21L101.71,2.98L101.71,2.98z M22.26,2.98l15.81,22.26H0L22.26,2.98L22.26,2.98L22.26,2.98z M44.73,29.57h33.63L62.04,95.04L44.73,29.57L44.73,29.57L44.73,29.57z M64.91,96.04l57.23-66.27l-38.01,0L64.91,96.04L64.91,96.04 L64.91,96.04z" />
  </svg>
);

const BananaIcon = ({ className, style }) => (
  <svg viewBox="0 0 1024 1024" fill="currentColor" className={className || "w-4 h-4"} style={style}>
    <path d="M609.3 909.7c-126.3 0-252.8-54-346.9-148.1C78.5 577.7 64.1 293.1 230.1 127l1.2-1.2c7.6-7.6 17-11.6 27.2-11.6 11.4 0 22.8 5.2 30.5 13.8 5.4 6 11.4 16.3 9.7 31.4-16.2 143.8 41.8 297.4 155.1 410.7C554 670.5 686.2 728 816.5 728c16.1 0 32.3-0.9 48.1-2.7 1.7-0.2 3.4-0.3 5.1-0.3 19.6 0 32 12.9 37 25 6.3 15 3.1 31-8.5 42.6l-1.2 1.2c-74.7 74.8-176.9 115.9-287.7 115.9z" />
    <path d="M258.5 130.5c6.8 0 13.7 3.1 18.4 8.4 4.6 5.1 6.5 11.4 5.6 18.7-16.8 148.7 42.9 307.2 159.8 424.1C545.6 685 682 744.3 816.5 744.3c16.7 0 33.5-0.9 49.9-2.8 1.1-0.1 2.2-0.2 3.3-0.2 11.6 0 19 7.8 22 15 2.2 5.2 4.4 15.4-5 24.9l-1.1 1.1C813.8 854 715.8 893.5 609.3 893.5c-59.7 0-118.9-12.1-176-35.9-59.2-24.8-112.9-60.9-159.4-107.5-86.4-86.4-137.2-198-142.9-314.1-5.6-114.9 33.6-220.6 110.5-297.5l1.1-1.1c4.7-4.6 10-6.9 15.9-6.9m-0.1-32.5c-13.5 0-27.2 5-38.6 16.3l-1.2 1.2C45.9 288.2 60.4 582.6 250.9 773.1 352.3 874.5 483.1 926 609.3 926c111 0 218.4-39.8 299.2-120.6l1.2-1.2c35.7-36 8.4-95.4-40.1-95.4-2.3 0-4.6 0.1-6.9 0.4-15.2 1.7-30.7 2.6-46.2 2.6-121.2 0-250.1-52-351.2-153-114-114-165.6-263.5-150.5-397.4C319 124.5 289.5 98 258.4 98z" />
    <path d="M729.2 811C444.6 811 213 579.4 213 294.8c0-9 7.3-16.3 16.3-16.3s16.3 7.3 16.3 16.3c0 266.7 217 483.8 483.8 483.8 9 0 16.3 7.3 16.3 16.3-0.2 8.9-7.5 16.1-16.5 16.1z" />
    <path d="M191.1 172.6c11.8-16.2 24.9-31.4 39-45.6l1.2-1.2c7.6-7.6 17.1-11.6 27.2-11.6 11.4 0 22.8 5.2 30.5 13.8 5.4 6 11.4 16.3 9.7 31.4-1.5 13.5-2.4 27.3-2.6 41.2l-105-28z" />
    <path d="M258.5 130.5c6.8 0 13.7 3.1 18.4 8.4 4.6 5.1 6.5 11.4 5.6 18.7-0.8 7.3-1.5 14.6-1.9 22.1L219 163.2c7.2-8.6 14.7-16.8 22.6-24.7l1.1-1.1c4.6-4.6 9.9-6.9 15.8-6.9m0-32.5c-13.5 0-27.2 5-38.6 16.3l-1.2 1.2c-20.7 20.7-38.6 43.1-53.9 66.8L312.5 222c-0.7-20.4 0-40.7 2.3-60.6C319 124.5 289.5 98 258.5 98z" />
  </svg>
);

const WidgetEmbed = () => {
  const { widgetId } = useParams();
  const targetId = widgetId;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [widgetConfig, setWidgetConfig] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [isValidEmbed, setIsValidEmbed] = useState(true);

  // Fetch widget configuration and associated reviews
  useEffect(() => {
    const fetchWidgetData = async () => {
      if (!targetId) {
        setError('No widget ID provided.');
        setLoading(false);
        return;
      }

      try {
        const { data: embedData, error: rpcError } = await supabase.rpc('get_public_widget_embed', { 
          p_widget_id: targetId 
        });

        if (rpcError || !embedData) throw new Error('Widget not found or access denied.');

        const config = embedData.widget.theme_config || {};
        const manualIds = embedData.widget.manual_selection_ids || [];

        let mappedReviews = embedData.reviews.map(r => ({
          id: r.id,
          source: r.source || 'Custom',
          authorName: r.client_name || 'Anonymous',
          authorRole: r.job_role || '',
          companyName: r.company_name || '',
          companyLogo: r.company_logo_url || '',
          clientAvatarUrl: r.client_avatar_url || '',
          youtubeLink: r.youtube_link || '',
          content: r.content || '',
          rating: r.rating || 5,
          date: r.created_at
        }));

        // 3. Apply Filters from theme_config
        if (config.manualSelection && manualIds.length > 0) {
          mappedReviews = mappedReviews.filter(r => manualIds.includes(r.id));
        }

        if (config.videoOnly) {
          mappedReviews = mappedReviews.filter(r => (r.source || '').toLowerCase() === 'youtube');
        } else if (config.textOnly) {
          mappedReviews = mappedReviews.filter(r => (r.source || '').toLowerCase() !== 'youtube');
        }

        if (config.keyword) {
          const kw = config.keyword.toLowerCase();
          mappedReviews = mappedReviews.filter(r => 
            (r.content && r.content.toLowerCase().includes(kw)) ||
            (r.authorName && r.authorName.toLowerCase().includes(kw))
          );
        }

        setWidgetConfig({
          ...config,
          layout: embedData.widget.layout || 'masonry',
          disableAutoResize: embedData.widget.disable_auto_resize ?? config.disableAutoResize ?? false,
          // Server-decided: true only for free-tier owners. Explicit check so a
          // stale response shape can't accidentally brand a paid user's widget.
          showBranding: embedData.show_branding === true
        });
        setTestimonials(mappedReviews);

      } catch (err) {
        console.error('Embed Load Error:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchWidgetData();
  }, [targetId]);

  const layout = widgetConfig?.layout || 'masonry';
  const bgColor = widgetConfig?.bgColor || 'transparent';
  const accentColor = widgetConfig?.accentColor || '#ea580c';
  const fontFamily = widgetConfig?.fontFamily || 'Plus Jakarta Sans';
  const ratingStyle = widgetConfig?.ratingStyle || 'stars';
  const disableAutoResize = widgetConfig?.disableAutoResize || false;

  // Dynamically inject the requested Google Font
  useGoogleFont(fontFamily);

  useEffect(() => {
    // Security check: Warn if accessed directly, but allow in dev environment or bypass
    if (window === window.top && import.meta.env.PROD) {
      setIsValidEmbed(false);
      return;
    }

    if (!loading && widgetConfig) {
      // Apply background and font
      document.body.style.backgroundColor = bgColor;
      document.documentElement.style.backgroundColor = bgColor;
      
      // If auto resize disabled, allow manual scrolling within the iframe
      document.body.style.overflow = disableAutoResize ? 'auto' : 'hidden';

      if (!disableAutoResize) {
        const container = document.getElementById('widget-container');
        
        const sendHeight = () => {
          if (window.parent && container) {
            // Add buffer for shadows/padding
            window.parent.postMessage({ type: 'WIDGET_RESIZE', height: container.scrollHeight + 32 }, '*');
          }
        };

        sendHeight();
        setTimeout(sendHeight, 100);
        setTimeout(sendHeight, 1000); // For late-loading images

        if (container) {
          const resizeObserver = new ResizeObserver(sendHeight);
          resizeObserver.observe(container);
          window.addEventListener('resize', sendHeight);
          
          return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', sendHeight);
            document.body.style.backgroundColor = '';
            document.documentElement.style.backgroundColor = '';
            document.body.style.overflow = '';
          };
        }
      }
    }
  }, [loading, widgetConfig, bgColor, disableAutoResize]);

  if (!isValidEmbed) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center" style={{ backgroundColor: '#0a0500' }}>
        <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-6">
          <SafeIcon icon={FiAlertTriangle} className="text-red-400 text-2xl" />
        </div>
        <h2 className="text-xl font-bold text-amber-50 mb-2">Embed Only Request</h2>
        <p className="text-zinc-400 max-w-md mx-auto leading-relaxed">
          This widget URL is strictly intended to be embedded within an iframe on external websites. It cannot be accessed directly.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ backgroundColor: bgColor !== 'transparent' ? bgColor : '#0a0500' }}>
        <div className="w-10 h-10 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !widgetConfig) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center" style={{ backgroundColor: '#0a0500' }}>
        <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-500/20 flex items-center justify-center mb-6">
          <SafeIcon icon={FiAlertTriangle} className="text-red-400 text-2xl" />
        </div>
        <h2 className="text-xl font-bold text-amber-50 mb-2">Widget Unavailable</h2>
        <p className="text-zinc-400 max-w-md mx-auto leading-relaxed">
          {error || 'Unable to load the requested widget.'}
        </p>
      </div>
    );
  }

  const renderRating = (rating) => {
    const r = rating || 5;
    if (ratingStyle === 'bananas') {
      return [...Array(r)].map((_, idx) => (
        <BananaIcon key={idx} className="w-3.5 h-3.5" style={{ color: accentColor, filter: `drop-shadow(0 0 4px ${accentColor}40)` }} />
      ));
    }
    if (ratingStyle === 'diamonds') {
      return [...Array(r)].map((_, idx) => (
        <DiamondIcon key={idx} className="w-3.5 h-3.5" style={{ color: accentColor, filter: `drop-shadow(0 0 4px ${accentColor}40)` }} />
      ));
    }
    if (ratingStyle === 'hearts') {
      return [...Array(r)].map((_, idx) => (
        <SafeIcon key={idx} icon={FiHeart} style={{ color: accentColor, fill: accentColor }} className="text-sm" />
      ));
    }
    return [...Array(r)].map((_, idx) => (
      <SafeIcon key={idx} icon={FiStar} style={{ color: accentColor, fill: accentColor }} className="text-sm" />
    ));
  };

  const ReviewMiniCard = ({ review }) => {
    const isVideo = review.source?.toLowerCase() === 'youtube' || !!review.youtubeLink;
    const videoId = isVideo ? getYoutubeVideoId(review.youtubeLink) : null;
    const isShort = isVideo ? isYoutubeShort(review.youtubeLink) : false;
    const isPureVideo = isVideo && (!review.content || review.content.trim() === '');

    return (
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="break-inside-avoid rounded-2xl backdrop-blur-md shadow-lg border transition-all duration-300 hover:-translate-y-1 h-full flex flex-col overflow-hidden"
        style={{ 
          backgroundColor: 'rgba(255, 255, 255, 0.03)',
          borderColor: 'rgba(255, 255, 255, 0.08)'
        }}
      >
        {videoId && (
          <div className="w-full flex-shrink-0 relative z-0 border-b" style={{ borderColor: 'rgba(255, 255, 255, 0.05)' }}>
            <LiteYouTubeEmbed 
              id={videoId} 
              title="YouTube Testimonial" 
              wrapperClass={`yt-lite ${isShort ? 'yt-short' : ''}`}
              noCookie={true}
              params="rel=0&modestbranding=1"
            />
            <div className="absolute top-3 right-3 px-2 py-1 bg-black/60 backdrop-blur-md rounded-md text-xs font-medium text-white/80 border border-white/10 flex items-center gap-1.5 pointer-events-none z-10">
              <SafeIcon icon={FiYoutube} style={{ color: accentColor }} /> YouTube
            </div>
          </div>
        )}

        {!isPureVideo && (
          <div className="p-5 sm:p-6 pb-2 flex-grow">
            <div className="flex justify-between items-start mb-4">
              <div className="flex gap-1 items-center">
                {renderRating(review.rating)}
              </div>
            </div>
            <p className="text-sm leading-relaxed mb-6 break-words" style={{ color: 'rgba(255, 255, 255, 0.85)' }}>
              {OPEN_QUOTE}{stripWrappingQuotes(review.content)}{CLOSE_QUOTE}
            </p>
          </div>
        )}

        <div className="flex items-center gap-3 mt-auto p-5 sm:p-6 border-t" style={{ borderColor: 'rgba(255, 255, 255, 0.05)', backgroundColor: 'rgba(0, 0, 0, 0.2)' }}>
          {/* Concurrent Avatars */}
          <div className="flex items-center -space-x-2 flex-shrink-0">
            {review.clientAvatarUrl ? (
              <div className="w-10 h-10 rounded-full border-2 relative z-20 overflow-hidden shadow-sm" style={{ borderColor: bgColor === 'transparent' ? '#18181b' : bgColor, backgroundColor: '#27272a' }}>
                <img src={review.clientAvatarUrl} alt={review.authorName} className="w-full h-full object-cover" />
              </div>
            ) : (!review.companyLogo && (
              <div className="w-10 h-10 rounded-full border-2 flex items-center justify-center relative z-20 shadow-sm" style={{ borderColor: bgColor === 'transparent' ? '#18181b' : bgColor, background: `linear-gradient(135deg, ${accentColor}40, rgba(0,0,0,0.5))` }}>
                <span className="text-white font-semibold">{review.authorName?.charAt(0) || 'A'}</span>
              </div>
            ))}
            {review.companyLogo && (
              <div className="w-10 h-10 rounded-full border-2 relative z-10 overflow-hidden shadow-sm bg-white" style={{ borderColor: bgColor === 'transparent' ? '#18181b' : bgColor }}>
                <img src={review.companyLogo} alt={review.companyName} className="w-full h-full object-contain p-0.5" />
              </div>
            )}
          </div>

          <div className="overflow-hidden flex-1 pl-1">
            <div className="text-white font-semibold text-sm truncate">
              {review.authorName || review.companyName || 'Anonymous'}
            </div>
            <div className="text-white opacity-60 text-[11px] truncate flex items-center gap-1.5">
              {review.authorRole && <span>{review.authorRole}</span>}
              {review.authorRole && review.companyName && <span>•</span>}
              {review.companyName && <span className="font-medium opacity-80">{review.companyName}</span>}
            </div>
          </div>

          {review.source && (
            <div className="px-2 py-1 rounded-md text-[10px] font-medium text-white/50 border border-white/10 flex items-center gap-1.5 flex-shrink-0" style={{ backgroundColor: 'rgba(0, 0, 0, 0.3)' }}>
              <SafeIcon icon={review.source.toLowerCase() === 'youtube' ? FiYoutube : FiMessageSquare} className="text-white/40" />
              {review.source}
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  const renderLayout = () => renderWidgetLayout({
    layout,
    items: testimonials,
    Card: (t) => <ReviewMiniCard review={t} />
  });

  return (
    <div id="widget-container" className="inline-block w-full p-4 sm:p-6 md:p-8 box-border" style={{ fontFamily }}>
      {renderLayout()}
      {widgetConfig?.showBranding && (
        <div className="w-full flex justify-center pt-5">
          <a
            href="https://momentoftruth.io"
            target="_blank"
            rel="noopener"
            className="inline-flex items-center gap-1.5 text-[11px] transition-opacity hover:opacity-100 opacity-70"
            style={{ color: 'rgba(140,140,140,0.9)' }}
          >
            Powered by <span className="font-semibold">Moment of Truth</span>
          </a>
        </div>
      )}
    </div>
  );
};

export default WidgetEmbed;